import { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { Building2, Plus, Edit2, Trash2, X, MapPin, Package, CreditCard } from 'lucide-react';

export default function SucursalesPage() {
    const [sucursales, setSucursales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ nombre: '', direccion: '', comuna: '' });

    useEffect(() => {
        fetchSucursales();
    }, []);

    const authHeaders = () => ({
        'Authorization': `Bearer ${localStorage.getItem('token')}`
    });

    const fetchSucursales = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/sucursales/stats`, { headers: authHeaders() });
            const data = await res.json();
            setSucursales(data);
        } catch (err) {
            console.error('Error fetching sucursales:', err);
        } finally {
            setLoading(false);
        }
    };

    const openModal = (sucursal = null) => {
        if (sucursal) {
            setEditingId(sucursal.id);
            setFormData({
                nombre: sucursal.nombre || '',
                direccion: sucursal.direccion || '',
                comuna: sucursal.comuna || ''
            });
        } else {
            setEditingId(null);
            setFormData({ nombre: '', direccion: '', comuna: '' });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingId 
                ? `${API_URL}/api/sucursales/${editingId}`
                : `${API_URL}/api/sucursales`;
            
            const res = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                setShowModal(false);
                fetchSucursales();
            } else {
                const error = await res.json();
                alert(error.error || 'Error al guardar');
            }
        } catch (err) {
            console.error('Error saving sucursal:', err);
            alert('Error al guardar la sucursal');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar esta sucursal?')) return;
        
        try {
            const res = await fetch(`${API_URL}/api/sucursales/${id}`, { method: 'DELETE', headers: authHeaders() });
            if (res.ok) {
                fetchSucursales();
            } else {
                const error = await res.json();
                alert(error.error || 'Error al eliminar');
            }
        } catch (err) {
            console.error('Error deleting sucursal:', err);
            alert('Error al eliminar la sucursal');
        }
    };

    return (
        <div className="page sucursales-page p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Building2 className="text-indigo-600" size={28} />
                        Gestión de Sucursales
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Administra las sucursales de tu negocio
                    </p>
                </div>
                <button 
                    className="btn-primary flex items-center gap-2"
                    onClick={() => openModal()}
                >
                    <Plus size={18} /> Nueva Sucursal
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-indigo-100 rounded-lg">
                            <Building2 className="text-indigo-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Total Sucursales</p>
                            <p className="text-2xl font-bold">{sucursales.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-green-100 rounded-lg">
                            <CreditCard className="text-green-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Total Cajas</p>
                            <p className="text-2xl font-bold">
                                {sucursales.reduce((sum, s) => sum + (parseInt(s.cajas_count) || 0), 0)}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-orange-100 rounded-lg">
                            <Package className="text-orange-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Total Bodegas</p>
                            <p className="text-2xl font-bold">
                                {sucursales.reduce((sum, s) => sum + (parseInt(s.bodegas_count) || 0), 0)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Cargando...</div>
                ) : sucursales.length === 0 ? (
                    <div className="p-8 text-center">
                        <Building2 size={48} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No hay sucursales creadas</p>
                        <button 
                            onClick={() => openModal()}
                            className="mt-4 btn-primary"
                        >
                            Crear primera sucursal
                        </button>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dirección</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Cajas</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Bodegas</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {sucursales.map(suc => (
                                <tr key={suc.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 text-sm text-gray-500">#{suc.id}</td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <Building2 size={18} className="text-indigo-500" />
                                            <span className="font-medium">{suc.nombre}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        <div className="flex items-center gap-1">
                                            <MapPin size={14} />
                                            {suc.direccion || '-'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                            {suc.cajas_count || 0}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                                            {suc.bodegas_count || 0}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-2 py-1 rounded-full text-xs ${
                                            suc.estado !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                        }`}>
                                            {suc.estado !== false ? 'Activa' : 'Inactiva'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex justify-center gap-2">
                                            <button
                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                                onClick={() => openModal(suc)}
                                                title="Editar"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                            <button
                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                                onClick={() => handleDelete(suc.id)}
                                                title="Eliminar"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">
                                {editingId ? 'Editar Sucursal' : 'Nueva Sucursal'}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Nombre *</label>
                                <input
                                    className="w-full p-2 border rounded-lg"
                                    required
                                    value={formData.nombre}
                                    onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                                    placeholder="Ej. Sucursal Central"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Dirección</label>
                                <input
                                    className="w-full p-2 border rounded-lg"
                                    value={formData.direccion}
                                    onChange={e => setFormData({ ...formData, direccion: e.target.value })}
                                    placeholder="Ej. Av. Principal 123"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Comuna</label>
                                <input
                                    className="w-full p-2 border rounded-lg"
                                    value={formData.comuna}
                                    onChange={e => setFormData({ ...formData, comuna: e.target.value })}
                                    placeholder="Ej. Santiago"
                                />
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button 
                                    type="button" 
                                    onClick={() => setShowModal(false)} 
                                    className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                                >
                                    {editingId ? 'Guardar Cambios' : 'Crear Sucursal'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
